import { createBrowserRouter, Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { useSession } from './lib/session';
import { AppLayout } from './components/AppLayout';
import { LoadingBlock } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordPages';
import { JoinPage } from './pages/JoinPage';
import { InvitePage } from './pages/InvitePage';
import { TeamsPage } from './pages/TeamsPage';
import { TeamPage } from './pages/TeamPage';
import { AccountPage } from './pages/AccountPage';
import { HelpPage } from './pages/HelpPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { ErrorPage } from './components/ErrorPage';
import { SchedulePage } from './pages/SchedulePage';
import { SessionPage } from './pages/SessionPage';
import { CourtsidePage } from './pages/CourtsidePage';
import { GameDayPage } from './pages/GameDayPage';
import { AdminLoginPage, AdminSetupPage } from './pages/admin/AdminAuthPages';
import { AdminLayout, RequireAdmin } from './components/admin/AdminLayout';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminAccountPage, AdminAccountsPage } from './pages/admin/AdminAccountsPage';
import { AdminTeamPage, AdminTeamsPage } from './pages/admin/AdminTeamsPage';
import { AdminClubPage, AdminClubsPage } from './pages/admin/AdminClubsPage';
import { ClubInvitePage, ClubSignupPage, ClubsPage, RegisterClubPage } from './pages/ClubsPage';
import { ClubPage } from './pages/ClubPage';
import { ClubTeamPage } from './pages/ClubTeamPage';
import { AdminLogPage } from './pages/admin/AdminLogPage';
import { AdminEmailPage } from './pages/admin/AdminEmailPage';
import { AdminServerPage } from './pages/admin/AdminServerPage';
import { AdminAdminsPage } from './pages/admin/AdminAdminsPage';
import { DrillsPage } from './pages/DrillsPage';
import { TemplatePage, TemplatesPage } from './pages/TemplatesPage';

/** Signed-in area: sends everyone else to sign in, then back here. */
function RequireSignedIn() {
  const { data, isLoading, isError } = useSession();
  const location = useLocation();
  if (isLoading) return <LoadingBlock />;
  if (isError || data?.status !== 'signed_in') {
    const next = location.pathname + location.search;
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Sign-in and sign-up pages: skip them when already signed in. */
function SignedOutOnly() {
  const { data, isLoading } = useSession();
  const [params] = useSearchParams();
  if (isLoading) return null;
  if (data?.status === 'signed_in') return <Navigate to={safeNext(params.get('next'))} replace />;
  return <Outlet />;
}

export function safeNext(next: string | null) {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export const router = createBrowserRouter([
  {
    errorElement: <ErrorPage />,
    children: [
  {
    element: <SignedOutOnly />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
    ],
  },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/invite/:token', element: <InvitePage /> },
  { path: '/join/:code', element: <JoinPage /> },
  { path: '/club-invite/:token', element: <ClubInvitePage /> },
  { path: '/clubs/signup', element: <ClubSignupPage /> },
  // Hoopify admin console: its own sign-in, separate from coaches.
  { path: '/admin/login', element: <AdminLoginPage /> },
  { path: '/admin/setup', element: <AdminSetupPage /> },
  {
    element: <RequireAdmin />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { path: '/admin', element: <AdminOverviewPage /> },
          { path: '/admin/accounts', element: <AdminAccountsPage /> },
          { path: '/admin/accounts/:id', element: <AdminAccountPage /> },
          { path: '/admin/teams', element: <AdminTeamsPage /> },
          { path: '/admin/teams/:id', element: <AdminTeamPage /> },
          { path: '/admin/clubs', element: <AdminClubsPage /> },
          { path: '/admin/clubs/:id', element: <AdminClubPage /> },
          { path: '/admin/log', element: <AdminLogPage /> },
          { path: '/admin/email', element: <AdminEmailPage /> },
          { path: '/admin/server', element: <AdminServerPage /> },
          { path: '/admin/admins', element: <AdminAdminsPage /> },
          { path: '/admin/*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  {
    element: <RequireSignedIn />,
    children: [
      { path: '/teams/:teamId/sessions/:sessionId/courtside', element: <CourtsidePage /> },
      { path: '/gameday', element: <GameDayPage /> },
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <SchedulePage /> },
          { path: '/drills', element: <DrillsPage /> },
          { path: '/templates', element: <TemplatesPage /> },
          { path: '/templates/:templateId', element: <TemplatePage /> },
          { path: '/teams', element: <TeamsPage /> },
          { path: '/teams/:teamId', element: <TeamPage /> },
          { path: '/teams/:teamId/sessions/:sessionId', element: <SessionPage /> },
          { path: '/clubs', element: <ClubsPage /> },
          { path: '/clubs/register', element: <RegisterClubPage /> },
          { path: '/clubs/:clubId', element: <ClubPage /> },
          { path: '/clubs/:clubId/teams/:clubTeamId', element: <ClubTeamPage /> },
          { path: '/account', element: <AccountPage /> },
          { path: '/help', element: <HelpPage /> },
          { path: '/help/:slug', element: <HelpPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
    ],
  },
]);
